import { create } from 'zustand';
import { VISIBLE_OVERLAYS } from '../config/topBarVisibility';

export type OverlayType =
  | 'heatmap' | 'footprint' | 'volumeProfile' | 'whaleMarkers'
  | 'smc' | 'levels' | 'vwap' | 'sessions' | 'structure' | 'context'
  | 'absorption' | 'execution' | 'checklist' | 'scanner';

export type ChartType = 'candle' | 'line';

const LOG_SCALE_KEY = 'dsa-log-scale';

function loadInitialLogScale(): boolean {
  try {
    return localStorage.getItem(LOG_SCALE_KEY) === 'true';
  } catch { /* ignore blocked storage */ }
  return false;
}

interface ChartState {
  visibleOverlays: Set<OverlayType>;
  visibleRange: { from: number; to: number } | null;
  crosshairPrice: number | null;
  crosshairTime: number | null;
  // Ratio (3.0 = 300%) used by FootprintCanvas's dedicated Imbalance highlight —
  // a footprint-level setting, not a separate overlay, so it lives here rather
  // than in visibleOverlays. Default 300% per the Stage 3 Imbalance spec.
  imbalanceRatio: number;
  // Minimum run length of consecutive same-side diagonally-imbalanced levels
  // (using the same computeDiagonalFlags()/imbalanceRatio above) for
  // FootprintCanvas to mark a "stacked imbalance". Default 3 per the Stage 3
  // Stacked Imbalance spec.
  stackSize: number;
  // Main price display. The candlestick series always exists (overlays map
  // prices through it); 'line' makes it transparent and shows a close-price
  // line series on top instead.
  chartType: ChartType;
  // Main chart's right price scale: logarithmic vs linear. Persisted. (Auto-scale
  // is transient view state and lives on the chart itself, not here.)
  logScale: boolean;
  // Chart Settings modal open state — in the store so both the toolbar gear and
  // the chart's right-click menu can open it.
  settingsOpen: boolean;

  toggleOverlay: (overlay: OverlayType) => void;
  setVisibleRange: (from: number, to: number) => void;
  setCrosshair: (price: number | null, time: number | null) => void;
  setImbalanceRatio: (ratio: number) => void;
  setStackSize: (size: number) => void;
  setChartType: (type: ChartType) => void;
  setLogScale: (v: boolean) => void;
  setSettingsOpen: (v: boolean) => void;
}

export const useChartStore = create<ChartState>((set) => ({
  // Only released tools (config/topBarVisibility.ts) can be on at startup, so
  // a hidden tool — which has no toggle to switch it off — is never enabled.
  // Full pro-app default was new Set(['heatmap', 'smc', 'context']).
  visibleOverlays: new Set<OverlayType>(VISIBLE_OVERLAYS),
  visibleRange: null,
  crosshairPrice: null,
  crosshairTime: null,
  imbalanceRatio: 3.0,
  stackSize: 3,
  chartType: 'candle',
  logScale: loadInitialLogScale(),
  settingsOpen: false,

  toggleOverlay: (overlay) =>
    set((state) => {
      const next = new Set(state.visibleOverlays);
      if (next.has(overlay)) {
        next.delete(overlay);
      } else {
        next.add(overlay);
      }
      return { visibleOverlays: next };
    }),

  setVisibleRange: (from, to) => set({ visibleRange: { from, to } }),
  setCrosshair: (crosshairPrice, crosshairTime) =>
    set({ crosshairPrice, crosshairTime }),
  setImbalanceRatio: (ratio) => set({ imbalanceRatio: ratio }),
  setStackSize: (size) => set({ stackSize: size }),
  setChartType: (chartType) => set({ chartType }),
  setLogScale: (logScale) => {
    set({ logScale });
    try { localStorage.setItem(LOG_SCALE_KEY, String(logScale)); } catch { /* ignore */ }
  },
  setSettingsOpen: (settingsOpen) => set({ settingsOpen }),
}));
